import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import '@aws-amplify/ui-react/styles.css';
import { captureException } from '@sentry/react';
import { Button, Spinner } from '@thedealersconcierge/components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '~/components/Modal';
import config from '~/config';
import { g } from '~/globals';
import { gqlMutationClient } from '~/lib/backend';
import './liveliness-detector-style-override.css';

/** This is a simplified file wrapper for File entity */
export type LivelinessFile = {
  referenceImageUrl: string;
  fileId: string;
};

export function LivelinessDetectionModal({
  isOpen,
  onClose,
  dealershipSlug,
  onAnalysisComplete,
  transactionId,
  userId
}: {
  isOpen: boolean;
  onClose: () => void;
  dealershipSlug: string;
  transactionId: string;
  userId: string;
  onAnalysisComplete: (data: LivelinessFile) => void;
}) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(true);
  const [sessionId, setSessionId] = useState<string>();
  const [showRetry, setShowRetry] = useState<boolean>(false);
  const [verificationStatus, setVerificationStatus] = useState<
    'NOT_STARTED' | 'SUCCEEDED' | 'FAILED'
  >('NOT_STARTED');
  const [resultUrl, setResultUrl] = useState<string>();

  /**
   * Will start a liveliness session and get AWS Session ID to be used in the
   * The AWS sessionId will be used in the Amplify Component
   */
  const startSession = async () => {
    setLoading(true);
    try {
      const res = await gqlMutationClient({
        dealershipSlug
      })({
        startLivenessAnalysis: [
          { transactionId: transactionId },
          {
            __typename: true,
            '...on GraphQLError': {
              message: true
            },
            '...on MutationStartLivenessAnalysisSuccess': {
              data: true
            }
          }
        ]
      });

      if (res.startLivenessAnalysis?.__typename === 'GraphQLError') {
        toast.error(res.startLivenessAnalysis.message);
      }

      if (
        res.startLivenessAnalysis?.__typename ===
        'MutationStartLivenessAnalysisSuccess'
      ) {
        setSessionId(res.startLivenessAnalysis.data);
      }
    } catch (error) {
      setVerificationStatus('FAILED');
      console.error(error);
      captureException(error);

      if (error instanceof Error && error.message) {
        toast.error(error.message);
      } else {
        toast.error(t('Failed to start verification. Please contact support.'));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Will be called once after the analysis is complete
   * It is called automatically by the Amplify Component
   */
  const finalizeSession = async () => {
    if (!sessionId) return;
    try {
      const res = await gqlMutationClient({
        dealershipSlug: dealershipSlug
      })({
        finalizeLivenessAnalysis: [
          {
            sessionId: sessionId
          },
          {
            __typename: true,
            '...on GraphQLError': {
              message: true
            },
            '...on MutationFinalizeLivenessAnalysisSuccess': {
              data: {
                id: true,
                status: true
              }
            }
          }
        ]
      });

      /**
       * Bail early if there is no result
       */
      if (
        res.finalizeLivenessAnalysis?.__typename !==
          'MutationFinalizeLivenessAnalysisSuccess' ||
        !res.finalizeLivenessAnalysis.data.id
      ) {
        toast.error('Verification failed. Please try again.');
        setVerificationStatus('FAILED');
        setShowRetry(true);
        return;
      }

      /**
       * Fetch the Result using the finalized id
       */
      const finalizedSessionId = res.finalizeLivenessAnalysis.data.id;
      const queryResult = await g.reg.queryClient.fetchQuery({
        queryKey: ['finalized-session', finalizedSessionId],
        queryFn: () => {
          return g.reg.tdcBackendClient.gqlQueryClient({
            dealershipSlug: dealershipSlug
          })({
            customer: [
              {
                transactionId: transactionId,
                userId: userId
              },
              {
                livelinessSession: [
                  {
                    filter: {
                      id: {
                        equals: finalizedSessionId
                      }
                    }
                  },
                  {
                    edges: {
                      node: {
                        status: true,
                        referenceImage: {
                          id: true,
                          url: true
                        },
                        confidence: true
                      }
                    }
                  }
                ]
              }
            ]
          });
        }
      });

      const livelinessResult =
        queryResult?.customer?.livelinessSession?.edges?.at(0)?.node;

      if (
        livelinessResult?.referenceImage?.id &&
        livelinessResult.referenceImage?.url &&
        livelinessResult?.confidence &&
        livelinessResult.confidence >= 90
      ) {
        setResultUrl(livelinessResult.referenceImage.url);
        setVerificationStatus('SUCCEEDED');
        toast.success(t('Verification Succeeded!'));
        onAnalysisComplete({
          fileId: livelinessResult?.referenceImage.id,
          referenceImageUrl: livelinessResult.referenceImage.url
        });
      } else {
        toast.error('Verification failed. Please try again.');
        setVerificationStatus('FAILED');
        setShowRetry(true);
      }
    } catch (error) {
      console.error(error);
      captureException(error);
    }
  };

  /**
   * On retry we make a new liveness session
   */
  const handleRetry = async () => {
    setShowRetry(false);
    await startSession();
  };

  useEffect(() => {
    void startSession();

    return () => {
      setSessionId(undefined);
    };
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="flex flex-col w-dvw h-dvh !max-w-full rounded-none pt-8"
    >
      <div className="relative flex flex-row justify-center items-center w-full">
        <div className="absolute left-2">
          <Button
            onClick={onClose}
            variant="GHOST"
            size="SMALL"
            label={t('Close')}
          />
        </div>

        <h3
          className="capitalize text-center"
          data-test-id={'desktop-webcam-modal'}
        >
          {t('Verify your Identity')}
        </h3>
      </div>

      <div className="flex flex-col items-center max-h-dvh overflow-y-scroll">
        {loading ? (
          <Spinner size="MEDIUM" color="BLUE" />
        ) : (
          <div className=" w-full max-w-[800px] min-w-[300px] px-spacing-04 h-full">
            {verificationStatus === 'NOT_STARTED' &&
              !showRetry &&
              sessionId && (
                <FaceLivenessDetector
                  sessionId={sessionId}
                  region={config.aws.region}
                  onAnalysisComplete={finalizeSession}
                  onError={(error) => {
                    console.error(error);
                    toast.error(
                      t('An error occurred during liveness detection.')
                    );
                  }}
                  displayText={{
                    startScreenBeginCheckText: `${t('Start Verification')}`,
                    hintCenterFaceText: `${t('Center your face')}`
                  }}
                  onUserCancel={() => {
                    setShowRetry(true);
                  }}
                  components={{
                    /**
                     * Hide the warning banner so the “Start Verification” button remains visible on mobile.
                     *
                     * This is done since <FaceLivelinessDetector/> doesn’t expose props to adjust it's video canvas.
                     */
                    PhotosensitiveWarning: (): React.JSX.Element => {
                      return <></>;
                    }
                  }}
                />
              )}

            {/* Display Result when verification is succeeded */}
            {verificationStatus === 'SUCCEEDED' && resultUrl && (
              <div className="flex flex-col justify-center items-center mt-spacing-10 gap-spacing-04">
                <img
                  src={resultUrl}
                  className="w-1/2 aspect-square object-contain"
                />

                <Button
                  label={t('Accept and continue')}
                  dataTestId="liveness-capture-accept"
                  onClick={onClose}
                />
              </div>
            )}

            {showRetry && (
              <div className="flex flex-col w-full items-center justify-center mt-spacing-10 gap-spacing-04">
                <p>{t('Verification failed')}</p>
                <Button onClick={handleRetry} label="Retry Verification" />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
